import api from './apiClient';

// Question types enum - matches backend QuestionType
export const QuestionType = {
  MultipleChoice: 0,
};

export const QuestionTypeLabels = {
  [QuestionType.MultipleChoice]: 'Multiple Choice',
};

/**
 * Helper to build FormData from an object, handling File objects and nested values.
 * @param {Object} data - Data object with fields to include
 * @param {File|null} image - Optional image file
 * @param {File|null} pdf - Optional PDF file
 * @returns {FormData}
 */
const buildFormData = (data, image = null, pdf = null) => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (value instanceof File) {
      formData.append(key, value);
    } else if (typeof value === 'boolean') {
      formData.append(key, value.toString());
    } else if (typeof value === 'number') {
      formData.append(key, value.toString());
    } else if (Array.isArray(value)) {
      // Handle arrays (e.g., choices, weights) - serialize as JSON
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value);
    }
  });

  if (image instanceof File) {
    formData.append('image', image);
  }

  if (pdf instanceof File) {
    formData.append('pdf', pdf);
  }

  return formData;
};

// ===================
// Quiz Collection CRUD
// ===================

/**
 * Get paginated list of quiz collections (owned/shared with current user)
 * @param {Object} params - Query params (page, pageSize, search, isPublished)
 * @param {Object} options - Request options
 */
export const getQuizCollections = async (params = {}, options = {}) => {
  return api.get('/quiz-collections', params, options);
};

/**
 * Get all published quiz collections for picker UIs.
 * @param {string} search - Optional search text
 * @param {Object} options - Request options
 */
export const getPublishedQuizLibrary = async (search = '', options = {}) => {
  return api.get('/quiz-collections/published-library', { search }, options);
};

/**
 * Get a single quiz collection with full details (topics, questions)
 * @param {number} id - Collection ID
 * @param {Object} options - Request options
 */
export const getQuizCollection = async (id, options = {}) => {
  return api.get(`/quiz-collections/${id}`, {}, options);
};

/**
 * Create a new quiz collection
 * @param {Object} data - Collection data { name }
 * @param {Object} options - Request options
 */
export const createQuizCollection = async (data, options = {}) => {
  return api.post('/quiz-collections', data, options);
};

/**
 * Update a quiz collection
 * @param {number} id - Collection ID
 * @param {Object} data - Collection data { name, isPublished }
 * @param {Object} options - Request options
 */
export const updateQuizCollection = async (id, data, options = {}) => {
  return api.put(`/quiz-collections/${id}`, data, options);
};

/**
 * Delete a quiz collection
 * @param {number} id - Collection ID
 * @param {Object} options - Request options
 */
export const deleteQuizCollection = async (id, options = {}) => {
  return api.delete(`/quiz-collections/${id}`, options);
};

/**
 * Publish a quiz collection
 * @param {number} id - Collection ID
 * @param {Object} options - Request options
 */
export const publishQuizCollection = async (id, options = {}) => {
  return api.post(`/quiz-collections/${id}/publish`, {}, options);
};

/**
 * Unpublish a quiz collection
 * @param {number} id - Collection ID
 * @param {Object} options - Request options
 */
export const unpublishQuizCollection = async (id, options = {}) => {
  return api.post(`/quiz-collections/${id}/unpublish`, {}, options);
};

// ===================
// Collection Topic CRUD
// ===================

/**
 * Add a topic to a collection
 * @param {number} collectionId - Collection ID
 * @param {Object} data - Topic data { name }
 * @param {Object} options - Request options
 */
export const addTopic = async (collectionId, data, options = {}) => {
  return api.post(`/quiz-collections/${collectionId}/topics`, data, options);
};

/**
 * Update a topic
 * @param {number} topicId - Topic ID
 * @param {Object} data - Topic data { name }
 * @param {Object} options - Request options
 */
export const updateTopic = async (topicId, data, options = {}) => {
  return api.put(`/quiz-collections/topics/${topicId}`, data, options);
};

/**
 * Delete a topic
 * @param {number} topicId - Topic ID
 * @param {Object} options - Request options
 */
export const deleteTopic = async (topicId, options = {}) => {
  return api.delete(`/quiz-collections/topics/${topicId}`, options);
};

// ===================
// Question CRUD
// ===================

/**
 * Add a question to a collection with optional image and PDF
 * @param {number} collectionId - Collection ID
 * @param {Object} data - Question data { type, code, body, isPublished, levelOfDifficultyId, topic, subtopic, law, version, choices, weights }
 * @param {File|null} image - Optional question image
 * @param {File|null} pdf - Optional question PDF
 * @param {Object} options - Request options
 */
export const addQuestion = async (collectionId, data, image = null, pdf = null, options = {}) => {
  const formData = buildFormData(data, image, pdf);
  return api.upload(`/quiz-collections/${collectionId}/questions`, formData, options);
};

/**
 * Update a question with optional image and PDF
 * @param {number} questionId - Question ID
 * @param {Object} data - Question data { code, body, isPublished, etc., clearImage, clearPdf }
 * @param {File|null} image - Optional new question image
 * @param {File|null} pdf - Optional new question PDF
 * @param {Object} options - Request options
 */
export const updateQuestion = async (questionId, data, image = null, pdf = null, options = {}) => {
  const formData = buildFormData(data, image, pdf);
  return api.uploadPut(`/quiz-collections/questions/${questionId}`, formData, options);
};

/**
 * Delete a question
 * @param {number} questionId - Question ID
 * @param {Object} options - Request options
 */
export const deleteQuestion = async (questionId, options = {}) => {
  return api.delete(`/quiz-collections/questions/${questionId}`, options);
};

/**
 * Reorder questions in a collection
 * @param {number} collectionId - Collection ID
 * @param {Array<number>} questionIds - Ordered array of question IDs
 * @param {Object} options - Request options
 */
export const reorderQuestions = async (collectionId, questionIds, options = {}) => {
  return api.post(`/quiz-collections/${collectionId}/questions/reorder`, { questionIds }, options);
};

/**
 * Set weights for a question
 * @param {number} questionId - Question ID
 * @param {Array<{topicId: number, weight: number}>} weights - Array of topic weights
 * @param {Object} options - Request options
 */
export const setQuestionWeights = async (questionId, weights, options = {}) => {
  return api.put(`/quiz-collections/questions/${questionId}/weights`, { weights }, options);
};

// ===================
// Bulk Import
// ===================

/**
 * Bulk import questions to a collection from JSON
 * @param {number} collectionId - Collection ID
 * @param {Array<Object>} questions - Array of question objects to import
 * @param {Object} options - Request options
 * @returns {Promise<{totalCount: number, successCount: number, errors: Array, validation: Object|null}>}
 */
export const bulkAddQuestions = async (collectionId, questions, options = {}) => {
  return api.post(`/quiz-collections/${collectionId}/questions/bulk`, { questions }, options);
};

/**
 * Get bulk import template with available mappings for a collection
 * @param {number} collectionId - Collection ID
 * @param {Object} options - Request options
 * @returns {Promise<{availableLevelsOfDifficulty: Array<string>, availableTopics: Array<string>, exampleQuestions: Array}>}
 */
export const getBulkImportTemplate = async (collectionId, options = {}) => {
  return api.get(`/quiz-collections/${collectionId}/questions/bulk-template`, options);
};

// ===================
// MC Choice CRUD
// ===================

/**
 * Add a choice to a question with optional image
 * @param {number} questionId - Question ID
 * @param {Object} data - Choice data { text, isCorrect }
 * @param {File|null} image - Optional choice image
 * @param {Object} options - Request options
 */
export const addChoice = async (questionId, data, image = null, options = {}) => {
  const formData = buildFormData(data, image);
  return api.upload(`/quiz-collections/questions/${questionId}/choices`, formData, options);
};

/**
 * Update a choice with optional image
 * @param {number} choiceId - Choice ID
 * @param {Object} data - Choice data { text, isCorrect, clearText, clearImage }
 * @param {File|null} image - Optional new choice image
 * @param {Object} options - Request options
 */
export const updateChoice = async (choiceId, data, image = null, options = {}) => {
  const formData = buildFormData(data, image);
  return api.uploadPut(`/quiz-collections/choices/${choiceId}`, formData, options);
};

/**
 * Delete a choice
 * @param {number} choiceId - Choice ID
 * @param {Object} options - Request options
 */
export const deleteChoice = async (choiceId, options = {}) => {
  return api.delete(`/quiz-collections/choices/${choiceId}`, options);
};

/**
 * Reorder choices for a question
 * @param {number} questionId - Question ID
 * @param {Array<number>} choiceIds - Ordered array of choice IDs
 * @param {Object} options - Request options
 */
export const reorderChoices = async (questionId, choiceIds, options = {}) => {
  return api.post(`/quiz-collections/questions/${questionId}/choices/reorder`, { choiceIds }, options);
};

// ===================
// Public Play
// ===================

/**
 * Get public info for a quiz collection (for play page)
 * @param {number} id - Collection ID
 * @param {Object} options - Request options
 */
export const getPublicQuizCollection = async (id, options = {}) => {
  return api.get(`/quiz-collections/${id}/play`, {}, options);
};

/**
 * Start playing a quiz - get randomized questions
 * @param {number} collectionId - Collection ID
 * @param {Object} params - Play params { questionCount, topicId }
 * @param {Object} options - Request options
 */
export const startQuizPlay = async (collectionId, params = {}, options = {}) => {
  return api.post(`/quiz-collections/${collectionId}/play`, params, options);
};

/**
 * Check answer for a question
 * @param {number} questionId - Question ID
 * @param {Array<number>} selectedChoiceIds - Array of selected choice IDs
 * @param {Object} options - Request options
 */
export const checkAnswer = async (questionId, selectedChoiceIds, options = {}) => {
  return api.post(`/quiz-collections/check-answer`, { questionId, selectedChoiceIds }, options);
};

/**
 * Finish a quiz and persist the attempt.
 * @param {number} collectionId - Collection ID
 * @param {Object} data - { playSessionToken, answers: [{ questionId, selectedChoiceIds, clientDurationMs }] }
 * @param {Object} options - Request options
 */
export const finishQuiz = async (collectionId, data, options = {}) => {
  return api.post(`/quiz-collections/${collectionId}/finish`, data, options);
};

// ===================
// Quiz Analytics (creator / admin)
// ===================

/**
 * Get aggregate statistics for a collection
 * @param {number} collectionId - Collection ID
 * @param {Object} options - Request options
 */
export const getQuizStatistics = async (collectionId, options = {}) => {
  return api.get(`/quiz-collections/${collectionId}/statistics`, {}, options);
};

/**
 * Get detailed analytics for a single question (timing distribution,
 * percentiles, correct/incorrect split). Backs the question-level
 * analytics modal in the collection editor.
 *
 * @param {number} collectionId - Collection ID
 * @param {number} questionId - Question ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Detailed question analytics payload.
 */
export const getQuizQuestionAnalytics = async (collectionId, questionId, options = {}) => {
  return api.get(
    `/quiz-collections/${collectionId}/questions/${questionId}/analytics`,
    {},
    options,
  );
};

/**
 * Get paged attempt list for a collection
 * @param {number} collectionId - Collection ID
 * @param {Object} params - { page, pageSize, search, topicFilterId, from, to }
 * @param {Object} options - Request options
 */
export const getQuizAttempts = async (collectionId, params = {}, options = {}) => {
  return api.get(`/quiz-collections/${collectionId}/attempts`, params, options);
};

/**
 * Get full detail for a single attempt
 * @param {number} collectionId - Collection ID
 * @param {number} attemptId - Attempt ID
 * @param {Object} options - Request options
 */
export const getQuizAttemptDetail = async (collectionId, attemptId, options = {}) => {
  return api.get(`/quiz-collections/${collectionId}/attempts/${attemptId}`, {}, options);
};

/**
 * Delete a single attempt
 * @param {number} collectionId - Collection ID
 * @param {number} attemptId - Attempt ID
 * @param {Object} options - Request options
 */
export const deleteQuizAttempt = async (collectionId, attemptId, options = {}) => {
  return api.delete(`/quiz-collections/${collectionId}/attempts/${attemptId}`, options);
};

/**
 * Bulk-delete attempts
 * @param {number} collectionId - Collection ID
 * @param {Array<number>} attemptIds - Array of attempt IDs to delete
 * @param {Object} options - Request options
 */
export const bulkDeleteQuizAttempts = async (collectionId, attemptIds, options = {}) => {
  return api.post(`/quiz-collections/${collectionId}/attempts/delete-bulk`, { attemptIds }, options);
};

/**
 * Download attempt list as CSV
 * @param {number} collectionId - Collection ID
 */
export const downloadQuizAttemptsExport = async (collectionId) => {
  const { blob, filename } = await api.download(`/quiz-collections/${collectionId}/attempts/export`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `quiz-attempts-${collectionId}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ===================
// Default Export
// ===================

export default {
  // Enums
  QuestionType,
  QuestionTypeLabels,
  // Collection CRUD
  getQuizCollections,
  getPublishedQuizLibrary,
  getQuizCollection,
  createQuizCollection,
  updateQuizCollection,
  deleteQuizCollection,
  publishQuizCollection,
  unpublishQuizCollection,
  // Topic CRUD
  addTopic,
  updateTopic,
  deleteTopic,
  // Question CRUD
  addQuestion,
  updateQuestion,
  deleteQuestion,
  reorderQuestions,
  setQuestionWeights,
  // Choice CRUD
  addChoice,
  updateChoice,
  deleteChoice,
  reorderChoices,
  // Public Play
  getPublicQuizCollection,
  startQuizPlay,
  checkAnswer,
  finishQuiz,
  // Analytics (creator / admin)
  getQuizStatistics,
  getQuizQuestionAnalytics,
  getQuizAttempts,
  getQuizAttemptDetail,
  deleteQuizAttempt,
  bulkDeleteQuizAttempts,
  downloadQuizAttemptsExport,
};
